import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    ReportProblem as ReportProblemIcon,
    CheckCircle as CheckCircleIcon,
    Gavel as GavelIcon,
    Home as HomeIcon,
    ListAlt as ListAltIcon,
    WarningAmber as WarningAmberIcon,
    Group as GroupIcon,
    ErrorOutline as ErrorOutlineIcon,
    AssignmentTurnedIn as AssignmentTurnedInIcon,
    Lock as LockIcon
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: '導入' },
    { id: 'causes', label: '家賃滞納が起きる主な原因' },
    { id: 'agreements', label: 'トラブルを防ぐための事前の取り決め' },
    { id: 'legal', label: '家賃滞納時の法的対応方法' },
    { id: 'caseStudies', label: '実際にあった家賃滞納トラブル事例' },
    { id: 'expertAdvice', label: '専門家からのアドバイスとまとめ' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#888',
                                        fontWeight: 'bold',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1,
                                        fontSize: '1.15rem'
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.97rem', textAlign: 'left' }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアの「家賃滞納」トラブルと解決策
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアは、家賃や光熱費を分担しながら自由で快適な暮らしを実現できる人気の住まい方です。しかし、複数人で生活費をシェアするからこそ「家賃の滞納」は避けては通れない大きなリスクのひとつ。<br />
                    滞納が発生すると、同居人との信頼関係が損なわれるだけでなく、賃貸契約や退去・法的措置など、複雑なトラブルへと発展することも少なくありません。<br /><br />
                    本記事では、家賃滞納が起こる主な原因から、未然にトラブルを防ぐ取り決め、実際の事例、そして法的な対応方法までを体系的に解説します。「もしも」の時のために、知っておくべき知識と具体的な解決策をまとめました。
                </Typography>
                <Box sx={{ my: 3 }}>
                    
                </Box>
            </Box>

            {/* 家賃滞納が起きる主な原因 */}
            <Box id="causes" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    家賃滞納が起きる主な原因
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアにおける家賃滞納は、単なる「お金の問題」にとどまらず、生活スタイルや人間関係、契約内容のあいまいさなど、さまざまな要因が複雑に絡み合って発生します。<br />
                    よくある主な原因を下記にまとめました。
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <WarningAmberIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText
                            primary="経済的なトラブルや収入減"
                            secondary="急な失業やアルバイトのシフト減、家族の事情などで家賃が払えなくなるケースが最も多いです。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <WarningAmberIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText
                            primary="支払い管理の甘さ・計画性不足"
                            secondary="お金の管理が苦手な人がいると、うっかり滞納や支払い忘れが起こることも。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <GroupIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="ルームメイト間のコミュニケーション不足"
                            secondary="全員の支払い状況をきちんと共有できていないと、滞納の発覚が遅れ、問題がこじれやすくなります。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <ErrorOutlineIcon color="error" />
                        </ListItemIcon>
                        <ListItemText
                            primary="契約・役割分担の曖昧さ"
                            secondary="「誰が家賃をまとめて払うか」や「滞納時の責任範囲」が明確でないと、トラブルが表面化しやすくなります。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <HomeIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="家主や管理会社との連絡ミス"
                            secondary="家賃振込口座の変更や、支払い方法の不一致といった" />
                    </ListItem>
                </List>
                <Box sx={{ my: 3 }}>
                    
                </Box>
            </Box>

            {/* トラブルを防ぐための事前の取り決め */}
            <Box id="agreements" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    トラブルを防ぐための事前の取り決め
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    家賃滞納トラブルは、そもそも「起こさない」ことが最も重要です。<br />
                    そのために不可欠なのが、ルームメイト同士・家主との間での明確なルール作りと、書面での取り決めです。
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%', borderLeft: '5px solid #1976d2' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <ListAltIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>最低限決めておきたい5つのポイント</Typography>
                                </Box>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon>
                                            <AssignmentTurnedInIcon color="success" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="家賃支払いの方法・期限"
                                            secondary="誰がまとめて振込するのか、支払い日は何日かを明文化"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <AssignmentTurnedInIcon color="success" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="遅延時の対応手順"
                                            secondary="1日でも遅れた場合の連絡ルールやペナルティを設定"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <AssignmentTurnedInIcon color="success" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="代表者・連絡担当者の明確化"
                                            secondary="家賃の管理責任者を決めておく"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <AssignmentTurnedInIcon color="success" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="滞納発生時の補填・責任分担"
                                            secondary="滞納者以外が建て替えるのか、全員で補填するのかを合意"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <AssignmentTurnedInIcon color="success" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="契約書・同意書の作成"
                                            secondary="口頭だけでなく書面を残すことでトラブル予防"
                                        />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%', bgcolor: '#f5f5f5' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <LockIcon color="secondary" sx={{ mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>おすすめチェックリスト</Typography>
                                </Box>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon>
                                            <CheckCircleIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="LINEグループや専用アプリで毎月の支払い状況を共有" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <CheckCircleIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="契約書のコピーを全員が保管" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <CheckCircleIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="支払い明細や振込履歴を必ず残す" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <CheckCircleIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="新しいルームメイト加入時にルールを再確認" />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Box sx={{ my: 3 }}>
                    <img
                        src="/images/blog/sharehouse-contract_roomshare-yachin-tainou-solution.jpg"
                        alt="テーブルの上に並ぶ複数の契約書とペン、ルームメイトたちが真剣な表情で書面を確認しているシーン。ホワイトとブルー基調の清潔な室内で誠実な雰囲気。"
                        style={{ width: '100%', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}
                    />
                </Box>
            </Box>

            {/* 家賃滞納時の法的対応方法 */}
            <Box id="legal" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    家賃滞納時の法的対応方法
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    万が一、家賃の滞納が発生し、話し合いでの解決が難しい場合は、法的措置も視野に入れましょう。まずは冷静に状況を整理し、次のステップを踏むことが大切です。
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <ReportProblemIcon color="error" />
                        </ListItemIcon>
                        <ListItemText
                            primary="内容証明郵便の送付"
                            secondary="滞納者に対して支払い請求の意思を明確に伝える最初の法的手段。証拠としても有効です。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <GavelIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="少額訴訟や調停の申し立て"
                            secondary="6０万円以下の家賃や損害賠償は少額訴訟で解決できる場合が多いです。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <ErrorOutlineIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText
                            primary="契約解除・強制退去請求"
                            secondary="賃貸契約の内容によっては、一定期間の滞納で契約解除や明け渡し請求が可能です。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="専門家（弁護士・司法書士等）への相談"
                            secondary="初期相談は無料の法律相談窓口もあるので、早めにプロに相談することをおすすめします。"
                        />
                    </ListItem>
                </List>
                <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 2, p: 2, mt: 2, mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                        <strong>注意：</strong>法的手段を取る際は、証拠（契約書、振込履歴、メッセージ記録等）を必ず整理・保管しておきましょう。<br />
                        また、家賃滞納が原因の人間関係トラブルは、悪化させる前に第三者（管理会社・調停機関）を介するのが得策です。
                    </Typography>
                </Box>
            </Box>

            {/* 実際にあった家賃滞納トラブル事例 */}
            <Box id="caseStudies" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    実際にあった家賃滞納トラブル事例
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    家賃滞納のリアルな事例を知ることで、どんなリスクが潜んでいるのか、どのような備えが必要かが具体的にイメージできます。<br />
                    代表的なケースを3つご紹介します。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Paper elevation={3} sx={{ p: 2, borderLeft: '5px solid #ef5350', height: '100%' }}>
                            <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                                <WarningAmberIcon color="error" sx={{ mr: 1 }} />
                                ケース1：支払い管理のずさんさで全員に損害
                            </Typography>
                            <Typography variant="body2">
                                3人で家賃を折半し、代表者がまとめて振込する約束だったが、代表者が数ヶ月分の家賃を使い込んでしまい、全員が連帯保証のもと損害賠償を請求されたケース。<br />
                                <strong>教訓：</strong>代表者任せにせず、支払い履歴を全員で確認できる仕組みを作るべき。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper elevation={3} sx={{ p: 2, borderLeft: '5px solid #42a5f5', height: '100%' }}>
                            <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                                <GroupIcon color="primary" sx={{ mr: 1 }} />
                                ケース2：話し合いで解決した理想例
                            </Typography>
                            <Typography variant="body2">
                                ひとりが失業し家賃が遅れてしまったが、グループLINEで事情を共有し、他のメンバーが一時的に立て替え。後日分割で返済し、トラブルに発展せず良好な関係を継続できた。<br />
                                <strong>教訓：</strong>日頃のコミュニケーションと信頼関係が最も重要。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper elevation={3} sx={{ p: 2, borderLeft: '5px solid #ab47bc', height: '100%' }}>
                            <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                                <ErrorOutlineIcon color="secondary" sx={{ mr: 1 }} />
                                ケース3：契約内容の不備による泥沼化
                            </Typography>
                            <Typography variant="body2">
                                ルームシェアの契約書が曖昧で、滞納者の責任範囲が不明確だったため、家主・同居人・滞納者の間で責任のなすりつけ合いに。最終的に調停に発展し、関係が完全に破綻。<br />
                                <strong>教訓：</strong>必ず書面で契約内容や責任分担を明確にしておくべき。
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* 専門家からのアドバイスとまとめ */}
            <Box id="expertAdvice" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    専門家からのアドバイスとまとめ
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアにおける家賃滞納トラブルは「防ぐこと」「早めに気づくこと」「冷静に対処すること」がポイントです。<br />
                    そのためには日頃からの信頼関係と、明確なルール作りが不可欠。どれだけ仲の良い友人同士でも、金銭が絡む以上は"万が一"を見据えた備えが大切です。
                </Typography>
                <Box sx={{ my: 2 }}>
                    <Chip label="家賃滞納" color="primary" variant="outlined" sx={{ mr: 1 }} />
                    <Chip label="ルームシェア契約" color="primary" variant="outlined" sx={{ mr: 1 }} />
                    <Chip label="法的トラブル" color="primary" variant="outlined" />
                </Box>
                <Paper elevation={0} sx={{ bgcolor: '#f1f8e9', borderLeft: '6px solid #4caf50', p: 2, mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        トラブル予防のための3つの鉄則
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText primary="必ず書面で契約・ルール作成！" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText primary="支払い状況を全員がリアルタイムで把握" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText primary="万が一のときは第三者や専門家に早めの相談" />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body1" sx={{ mt: 3 }}>
                    ルームシェアは、信頼と協力の上に成り立つ素晴らしい住まい方です。家賃滞納というリスクを正しく理解し、賢く備えることで、トラブルを未然に防ぎ、快適な共同生活を楽しみましょう。
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-yachin-tainou-solution',
    slug: 'roomshare-yachin-tainou-solution',
    title: 'ルームシェアの「家賃滞納」トラブルと解決策',
    excerpt: 'ルームシェアで発生しやすい家賃滞納の原因や、事前の取り決め、法的対応、実際のトラブル事例までを徹底解説。トラブルを未然に防ぐための実践的なポイントと対策をまとめました。',
    coverImage: '/images/blog/cover-sharehouse-trouble_roomshare-yachin-tainou-solution.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。家賃や生活費を分担しながらも信頼関係を築く共同生活の温かい雰囲気を表現。',
    publishedAt: '2024-06-15T09:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '賃貸トラブル', '生活ノウハウ'],
    tags: ['家賃滞納', 'ルームシェア', '契約トラブル', '法的対応'],
    author: {
        name: '山本 純平',
        avatar: '/images/avatars/yamamoto-junpei.jpg',
        bio: '宅地建物取引士資格を保有し、シェアハウス運営・賃貸仲介に10年以上携わる。現場での豊富な実例をもとに、トラブル予防や快適な共同生活のヒントを発信中。',
        profilePicture: '/images/avatars/yamamoto-junpei.jpg'
    },
    viewCount: 1200,
    contentComponent: PostContent
};

export default post;